import { CampaignPlatform } from "../types/Platforms";

// Base platform icons (using lowercase keys)
export const platformIcons: Record<CampaignPlatform, string> = {
  x: "/icons/campaigns/x.svg",
  meta: "/icons/campaigns/meta.svg",
  linkedin: "/icons/campaigns/linkedin.svg",
  dv360: "/icons/campaigns/dv360.svg",
  instagram: "/icons/campaigns/instagram.svg",
  tiktok: "/icons/campaigns/tiktok.svg",
  natads: "/icons/campaigns/beapolo.svg",
  ctv: "/icons/campaigns/stream.svg",
};

// Additional platform icons not in the CampaignPlatform type
export const additionalPlatformIcons: Record<string, string> = {
  taboola: "/icons/campaigns/beapolo.svg",
  outbrain: "/icons/campaigns/beapolo.svg",
};

// Black-only icons: the theme turns them white in dark mode
export const MONOCHROME_PLATFORM_ICONS = [
  "/icons/campaigns/natads.svg",
  "/icons/campaigns/audio.svg",
  "/icons/campaigns/dooh.svg",
  "/icons/campaigns/stream.svg",
  "/icons/campaigns/video.svg",
];

export const isMonochromePlatformIcon = (iconPath: string): boolean =>
  MONOCHROME_PLATFORM_ICONS.includes(iconPath);

// Third-party platforms keep their own logo; the rest use the BeApolo product icons
const OWN_LOGO_ICONS: Record<string, string> = {
  meta: "/icons/campaigns/meta.svg",
  facebook: "/icons/campaigns/facebook.png",
  facebook_and_instagram: "/icons/campaigns/meta.svg",
  instagram: "/icons/campaigns/instagram.svg",
  tiktok: "/icons/campaigns/tiktok.svg",
  linkedin: "/icons/campaigns/linkedin.svg",
  x: "/icons/campaigns/x.svg",
  x_twitter: "/icons/campaigns/x.svg",
  twitter: "/icons/campaigns/x.svg",
  pinterest: "/icons/campaigns/pinterest.webp",
  google_ads: "/icons/campaigns/google-ads.png",
  googleads: "/icons/campaigns/google-ads.png",
  tres_cat: "/icons/campaigns/3cat.png",
  "3cat": "/icons/campaigns/3cat.png",
  mediaset: "/icons/campaigns/mediaset.png",
  atresmedia: "/icons/campaigns/atresmedia.png",
};

/**
 * Gets the platform icon path
 * @returns The path to the platform icon (third-party platforms get their own
 * logo, the rest the BeApolo product icon)
 */
export const getPlatformIconPath = (platform: string): string => {
  const normalizedPlatform = platform.toLocaleLowerCase();

  if (OWN_LOGO_ICONS[normalizedPlatform]) {
    return OWN_LOGO_ICONS[normalizedPlatform];
  }
  // GOOGLE_ADS_SEM, GOOGLE_ADS_PMAX, GOOGLE_ADS_DEMANDGEN...
  if (normalizedPlatform.startsWith("google_ads")) {
    return OWN_LOGO_ICONS.google_ads;
  }
  if (
    normalizedPlatform === "spotify_ads" ||
    normalizedPlatform === "spotify"
  ) {
    return "/icons/campaigns/audio.svg";
  }
  if (
    normalizedPlatform === "taboola" ||
    normalizedPlatform === "outbrain" ||
    normalizedPlatform === "natads"
  ) {
    return "/icons/campaigns/natads.svg";
  }
  if (normalizedPlatform === "hivestack") {
    return "/icons/campaigns/dooh.svg";
  }
  if (normalizedPlatform === "amazon_dsp" || normalizedPlatform === "amazon") {
    return "/icons/campaigns/video.svg";
  }
  if (normalizedPlatform === "ctv") {
    return "/icons/campaigns/stream.svg";
  }
  // Every other platform uses the BeApolo logo, as requested by the client
  return "/icons/campaigns/beapolo.svg";
};

/**
 * Gets the display name for a platform according to client requirements:
 * - Taboola and Outbrain should display as "NATADS"
 * - All other platforms should display as "BeApolo"
 *
 * @param platform - Original platform name
 * @returns The display name to show in the UI
 */
export const getPlatformDisplayName = (platform: string): string => {
  const normalizedPlatform = platform.toLowerCase();

  // Taboola and Outbrain should display as NATADS
  if (normalizedPlatform === "taboola" || normalizedPlatform === "outbrain") {
    return "NATADS";
  }
  if (
    normalizedPlatform === "spotify_ads" ||
    normalizedPlatform === "spotify"
  ) {
    return "Audio";
  }
  if (normalizedPlatform === "dv360" || normalizedPlatform === "beapolo") {
    return "BeApolo";
  }
  if (normalizedPlatform === "hivestack") {
    return "DOOH";
  }
  if (normalizedPlatform === "ctv") {
    return "CTV";
  }
  if (normalizedPlatform === "amazon_dsp" || normalizedPlatform === "amazon") {
    return "Amazon";
  }
  if (
    normalizedPlatform === "googleads" ||
    normalizedPlatform.startsWith("google_ads")
  ) {
    return "Google Ads";
  }
  if (normalizedPlatform === "meta") {
    return "Meta";
  }
  if (normalizedPlatform === "facebook") {
    return "Facebook";
  }
  if (normalizedPlatform === "pinterest") {
    return "Pinterest";
  }
  if (normalizedPlatform === "tres_cat" || normalizedPlatform === "3cat") {
    return "3Cat";
  }
  if (normalizedPlatform === "mediaset") {
    return "Mediaset";
  }
  if (normalizedPlatform === "atresmedia") {
    return "Atresmedia";
  }
  if (normalizedPlatform === "tiktok") {
    return "TikTok";
  }
  if (normalizedPlatform === "instagram") {
    return "Instagram";
  }
  if (normalizedPlatform === "linkedin") {
    return "LinkedIn";
  }
  if (
    normalizedPlatform === "x" ||
    normalizedPlatform === "x_twitter" ||
    normalizedPlatform === "twitter"
  ) {
    return "X";
  }
  if (normalizedPlatform === "reddit") {
    return "Reddit";
  }
  return "Natads";
};

interface PlatformIconMeta {
  iconPath: string;
  displayName: string;
  normalizedKey: string;
}

const BRIEFING_PLATFORM_MAP: {
  matcher: (value: string) => boolean;
  key: string;
}[] = [
  // FACEBOOK_AND_INSTAGRAM goes before FACEBOOK and INSTAGRAM: it is Meta
  {
    matcher: (value) => value === "facebook_and_instagram" || value === "meta",
    key: "meta",
  },
  { matcher: (value) => value.includes("facebook"), key: "facebook" },
  { matcher: (value) => value.includes("pinterest"), key: "pinterest" },
  { matcher: (value) => value.startsWith("google_ads"), key: "google_ads" },
  {
    matcher: (value) => value === "tres_cat" || value.includes("3cat"),
    key: "tres_cat",
  },
  { matcher: (value) => value.includes("mediaset"), key: "mediaset" },
  { matcher: (value) => value.includes("atresmedia"), key: "atresmedia" },
  { matcher: (value) => value.includes("instagram"), key: "instagram" },
  { matcher: (value) => value.includes("tiktok"), key: "tiktok" },
  { matcher: (value) => value.includes("linkedin"), key: "linkedin" },
  {
    matcher: (value) => value === "x" || value.includes("twitter"),
    key: "x",
  },
  { matcher: (value) => value.includes("spotify"), key: "spotify" },
  { matcher: (value) => value.includes("amazon"), key: "amazon_dsp" },
  {
    matcher: (value) => value.includes("hivestack") || value.includes("dooh"),
    key: "hivestack",
  },
  {
    matcher: (value) =>
      value.includes("ctv") ||
      value.includes("ott") ||
      value.includes("samsung"),
    key: "ctv",
  },
  {
    matcher: (value) => value.includes("netflix") || value.includes("disney"),
    key: "ctv",
  },
  {
    matcher: (value) =>
      value.includes("taboola") ||
      value.includes("outbrain") ||
      value.includes("natads"),
    key: "natads",
  },
  {
    matcher: (value) =>
      value.includes("dv360") ||
      value.includes("beapolo") ||
      value.includes("youtube"),
    key: "dv360",
  },
];

/**
 * Normalizes a backend briefing platform (e.g. BEAPOLO_DOOH, NATADS)
 * into the icon/display metadata used across the UI.
 */
export const resolveBriefingPlatformIcon = (
  platform?: string | null
): PlatformIconMeta => {
  if (!platform) {
    const fallbackIcon = getPlatformIconPath("natads");
    return {
      iconPath: fallbackIcon,
      displayName: getPlatformDisplayName("natads"),
      normalizedKey: "natads",
    };
  }

  const normalized = platform.toLowerCase();
  const matched =
    BRIEFING_PLATFORM_MAP.find((entry) => entry.matcher(normalized))?.key ??
    "natads";

  return {
    iconPath: getPlatformIconPath(matched),
    displayName: getPlatformDisplayName(matched),
    normalizedKey: matched,
  };
};
